import { analyticsClient } from "./client";
import type { AnalyticsEvent } from "./events";

/**
 * ORGANIZATION_GROUP_TYPE is the PostHog group type organizations report under.
 * Group types are created by first use and are effectively permanent, so this
 * string is the schema — it lives here once rather than at each call site.
 */
const ORGANIZATION_GROUP_TYPE = "organization";

/**
 * AnalyticsProperties is the property bag attached to a person, a group, or an
 * event. The package deliberately takes primitives rather than the platform's
 * domain types: it is a leaf that imports no other workspace package, so the
 * caller owns the vocabulary and passes already-typed domain values in.
 */
export type AnalyticsProperties = Record<string, boolean | number | string | null | undefined>;

/**
 * identifyUser links every subsequent event on this device to a known person.
 * distinctId must be the stable account id, never an email or a display name.
 */
export const identifyUser = (distinctId: string, properties: AnalyticsProperties): void => {
  analyticsClient()?.identify(distinctId, properties);
};

/**
 * identifyOrganization scopes subsequent events to an organization and updates
 * that organization's properties. PostHog needs at least one group property
 * before a group becomes visible, so callers pass the org name rather than the id
 * alone. Calling it again with a different id re-scopes the session, which is how
 * an organization switch is recorded.
 */
export const identifyOrganization = (
  organizationId: string,
  properties: AnalyticsProperties,
): void => {
  analyticsClient()?.group(ORGANIZATION_GROUP_TYPE, organizationId, properties);
};

/**
 * captureEvent records a custom event. The name is constrained to the closed
 * vocabulary in events.ts, so a typo is a compile error rather than an orphaned
 * series in a dashboard.
 */
export const captureEvent = (event: AnalyticsEvent, properties?: AnalyticsProperties): void => {
  analyticsClient()?.capture(event, properties);
};

/**
 * resetAnalytics unlinks this device from the identified person and clears the
 * group scope. It must run on logout, otherwise the next person to sign in on a
 * shared browser inherits the previous one's identity.
 */
export const resetAnalytics = (): void => {
  analyticsClient()?.reset();
};
